import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { BackofficeInterviewLink } from '@nava/backoffice-bff-contracts';

export function EditInterviewLinkModal({
  link,
  onSave,
  onClose,
}: {
  link: BackofficeInterviewLink;
  onSave: (hostname: string) => Promise<unknown>;
  onClose: () => void;
}) {
  const [hostname, setHostname] = useState(link.hostname ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    dialog.current?.showModal();
  }, []);
  const selected = link.options.find((option) => option.hostname === hostname);
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!selected || saving || hostname === link.hostname) return;
    setSaving(true);
    setError(null);
    try {
      await onSave(hostname);
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Failed to update interview link');
    } finally {
      setSaving(false);
    }
  }
  return (
    <dialog
      ref={dialog}
      aria-labelledby="interview-link-settings-title"
      className="fixed inset-0 m-auto w-[calc(100%_-_2rem)] max-w-lg rounded-xl border border-neutral-200 bg-white p-6 shadow-xl backdrop:bg-neutral-950/40"
      onCancel={(event) => {
        event.preventDefault();
        if (!saving) onClose();
      }}
    >
      <form onSubmit={submit} className="space-y-5">
        <h2 id="interview-link-settings-title" className="text-title font-semibold">
          Interview link settings
        </h2>
        <div>
          <label htmlFor="study-interview-hostname" className="block text-sm font-semibold">
            Hostname
          </label>
          <select
            id="study-interview-hostname"
            className="focus-ring mt-2 w-full rounded-lg border border-neutral-200 bg-white p-2"
            value={hostname}
            disabled={saving}
            onChange={(event) => {
              setHostname(event.target.value);
              setError(null);
            }}
          >
            {!link.options.some((option) => option.hostname === link.hostname) && link.hostname ? (
              <option value={link.hostname} disabled>
                {link.hostname} (current)
              </option>
            ) : null}
            {link.options.map((option) => (
              <option key={option.hostname} value={option.hostname}>
                {option.hostname}
              </option>
            ))}
          </select>
        </div>
        <div>
          <p className="text-sm font-semibold">Interview link preview</p>
          <p className="mt-2 break-all text-sm text-neutral-600">{selected?.url ?? link.url}</p>
        </div>
        {error ? (
          <p role="alert" className="text-sm text-red-700">
            {error}
          </p>
        ) : null}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="focus-ring rounded-lg border border-neutral-200 px-4 py-2 text-sm"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!selected || hostname === link.hostname || saving}
            className="focus-ring rounded-lg bg-backoffice-500 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>
    </dialog>
  );
}
